import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { Button } from '@structural-climbing/ui'
import type { ProfileFixField } from './profileFixLink'

interface Props {
  /** Whether the field the member came here for is now saved. */
  done: boolean
  returnTo: string
  /** Which field brought them here. A phone is asked for by the gym, not by a competition. */
  field?: ProfileFixField
}

/**
 * Shown at the top of the profile when a member was sent here to fill in one
 * field (from a competition they could not yet enter, or from a gym that needs
 * a phone number). It says what to do, then turns into "saved, go back" the
 * moment the field is filled, so nobody has to find their way back by hand.
 */
export function ProfileReturnBanner({ done, returnTo, field }: Props) {
  const { t } = useTranslation()
  const suffix = field === 'phone' ? 'Phone' : ''

  // Solid underneath: it sticks over the form while the member scrolls, and a
  // translucent card let the field labels show through the text.
  return (
    <div
      role="status"
      className="sticky top-2 z-20 overflow-hidden rounded-2xl border border-divider bg-content1 shadow-lg"
    >
      <div
        className={`flex flex-col gap-3 border-l-4 p-4 transition-colors sm:flex-row sm:items-center ${
          done ? 'border-success bg-success/10' : 'border-primary bg-primary/5'
        }`}
      >
        <p className="flex-1 text-sm font-medium">
          {done ? t(`profile.returnHintDone${suffix}`) : t(`profile.returnHintPending${suffix}`)}
        </p>
        <Button
          as={Link}
          to={returnTo}
          color={done ? 'success' : 'primary'}
          variant={done ? 'solid' : 'flat'}
          className="shrink-0 font-semibold"
          startContent={<span aria-hidden="true">←</span>}
        >
          {t(`profile.returnCta${suffix}`)}
        </Button>
      </div>
    </div>
  )
}
